import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Download, Trash2, RefreshCw, Inbox } from "lucide-react";

export default function AdminLeads() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        nav("/admin/login");
        return;
      }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Remover este lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      toast.success("Lead removido");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onExport = async () => {
    try {
      const token = localStorage.getItem("pt_token");
      const res = await fetch(`${API}/leads/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erro ao exportar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-alabaster">
      <header className="sticky top-0 z-30 bg-onyx text-alabaster">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="leads-back" className="inline-flex items-center gap-2 hover:text-gold transition-colors">
              <ArrowLeft size={16} />
              <span className="text-xs tracking-[0.2em] uppercase">Voltar</span>
            </Link>
            <span className="font-serif text-xl">Leads Capturados</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="leads-refresh" className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-[0.2em] uppercase hover:text-gold transition-colors">
              <RefreshCw size={14} />
            </button>
            <button onClick={onExport} data-testid="leads-export" className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-onyx text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alabaster transition-colors">
              <Download size={14} /> Exportar CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div data-testid="leads-loading" className="py-16 text-center text-onyx/60">Carregando…</div>
        ) : leads.length === 0 ? (
          <div data-testid="leads-empty" className="py-20 text-center">
            <Inbox size={40} className="mx-auto text-onyx/30 mb-4" />
            <p className="font-serif text-2xl text-onyx">Nenhum lead ainda</p>
            <p className="text-sm text-onyx/60 mt-2">
              Quando alguém preencher o formulário na landing page, aparecerá aqui.
            </p>
          </div>
        ) : (
          <div data-testid="leads-list" className="bg-white border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-alabaster text-onyx/60 text-xs tracking-[0.15em] uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">WhatsApp</th>
                  <th className="text-left px-4 py-3">E-mail</th>
                  <th className="text-left px-4 py-3">Origem</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-border hover:bg-alabaster/50">
                    <td className="px-4 py-3 text-onyx/60">{new Date(l.created_at).toLocaleString("pt-BR")}</td>
                    <td className="px-4 py-3 font-medium">{l.name || "—"}</td>
                    <td className="px-4 py-3">
                      {l.phone ? (
                        <a className="text-gold hover:underline" href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                          {l.phone}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">{l.email || "—"}</td>
                    <td className="px-4 py-3 text-xs text-onyx/60">{l.source}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => onDelete(l.id)} data-testid={`lead-delete-${l.id}`} className="text-onyx/40 hover:text-destructive p-1">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-sm text-onyx/60">Total: {leads.length} {leads.length === 1 ? "lead" : "leads"}</p>
      </main>
    </div>
  );
}
